import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Fish } from '../types/fish';
import { stableStringify } from '@Utils/searchParams';

type MembersResponse = { data: Fish[]; meta: Record<string, any> };
type DefaultError = unknown;

type UseMembersParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service.getMembers */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

/**
 * useMembers
 *
 * @example
  const venueQuery = useMembers(
    {
      venueId,
      searchParams: {
        include: 'fishProfiles',
        }
     },
    { staleTime: 60_000 }
  );
 */
export function useMembers<TData = MembersResponse, TError = DefaultError>(
  { searchParams, enabled = true }: UseMembersParams = {},
  options?: Omit<
    UseQueryOptions<MembersResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<MembersResponse, TError, TData>({
    queryKey: ['members', stableParams],
    enabled: enabled,
    queryFn: async () => {
      // membersId is truthy when enabled is true (see enabled gate above)
      return service.getMembersWithMeta(searchParams ?? {});
    },
    // caller can pass select, staleTime, gcTime, retry, refetchOnWindowFocus, etc.
    ...(options as any),
  });
}
